import { Container } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ManualBreadcrumb from '../../components/manual/ManualBreadcrumb';
import ManualSection from '../../components/manual/ManualSection';
import ManualStep from '../../components/manual/ManualStep';
import ManualAlert from '../../components/manual/ManualAlert';
import LoadingSpinner from '../../components/common/LoadingSpinner';

function ManualUsuariosPage() {
  const [perfis, setPerfis] = useState([]);
  const [perfisLoading, setPerfisLoading] = useState(true);
  const [perfisError, setPerfisError] = useState(false);
  const { canAccess } = useAuth();

  // Função para obter permissões padrão quando a API falha
  const getDefaultPermissions = (perfilNome) => {
    const defaultPermissions = {
      'Administrador': [
        { nome: 'Acesso Total ao Sistema' },
        { nome: 'Gerenciar Usuários' },
        { nome: 'Configurar Sistema' },
        { nome: 'Exportar Dados' },
        { nome: 'Gerenciar Permissões' }
      ],
      'Operador': [
        { nome: 'Ver Adolescentes' },
        { nome: 'Criar Adolescente' },
        { nome: 'Editar Adolescente' },
        { nome: 'Ver Movimentações' },
        { nome: 'Criar Movimentação' },
        { nome: 'Painel de Controle' },
        { nome: 'Ver Atendimentos' },
        { nome: 'Criar Atendimento' }
      ],
      'Consulta': [
        { nome: 'Ver Adolescentes' },
        { nome: 'Ver Movimentações' },
        { nome: 'Painel de Controle' },
        { nome: 'Ver Relatórios' }
      ],
      'Técnico': [
        { nome: 'Ver Adolescentes' },
        { nome: 'Criar Adolescente' },
        { nome: 'Ver Movimentações' },
        { nome: 'Criar Movimentação' }
      ],
      'MPDFT': [
        { nome: 'Ver Adolescentes' },
        { nome: 'Ver Relatórios' },
        { nome: 'Exportar Relatórios' }
      ],
      'DPDF': [
        { nome: 'Ver Adolescentes' },
        { nome: 'Ver Relatórios' },
        { nome: 'Exportar Relatórios' }
      ],
      'Especialista': [
        { nome: 'Ver Atendimentos' },
        { nome: 'Criar Atendimento' },
        { nome: 'Editar Atendimento' }
      ]
    };

    return defaultPermissions[perfilNome] || [
      { nome: 'Acesso Básico ao Sistema' }
    ];
  };

  // Função para converter nomes técnicos de permissões em descrições amigáveis
  // Nota: Esta função é mantida como fallback, mas o sistema agora usa descriptions do banco de dados
  const prettyPermissionName = (permissionName) => {
    const prettyNames = {
      // Adolescentes (5 permissões)
      'adolescentes.view': 'Visualizar adolescentes',
      'adolescentes.create': 'Criar adolescente',
      'adolescentes.edit': 'Editar adolescente',
      'adolescentes.delete': 'Excluir adolescente',
      'adolescentes.export': 'Exportar adolescentes',

      // Movimentações (5 permissões)
      'movimentacoes.view': 'Visualizar movimentações',
      'movimentacoes.create': 'Criar movimentação',
      'movimentacoes.edit': 'Editar movimentação',
      'movimentacoes.delete': 'Excluir movimentação',
      'movimentacoes.export': 'Exportar movimentações',

      // Atendimentos (4 permissões)
      'atendimentos.view': 'Visualizar atendimentos',
      'atendimentos.create': 'Criar atendimento',
      'atendimentos.edit': 'Editar atendimento',
      'atendimentos.delete': 'Excluir atendimento',

      // Usuários (4 permissões)
      'usuarios.view': 'Visualizar usuários',
      'usuarios.create': 'Criar usuário',
      'usuarios.edit': 'Editar usuário',
      'usuarios.delete': 'Excluir usuário',

      // Perfis (1 permissão)
      'perfis.manage': 'Gerenciar perfis e permissões',

      // Especialistas (4 permissões)
      'especialistas.view': 'Visualizar especialistas',
      'especialistas.create': 'Criar especialista',
      'especialistas.edit': 'Editar especialista',
      'especialistas.delete': 'Excluir especialista',

      // Relatórios (2 permissões)
      'relatorios.view': 'Visualizar relatórios',
      'relatorios.export': 'Exportar relatórios',

      // Logs (1 permissão)
      'admin.logs': 'Visualizar logs do sistema',

      // Painel (1 permissão)
      'movimentacoes.reopen': 'Reabrir movimentações',

      // Configurações - Tabelas de Apoio (7 permissões)
      'config.perfis': 'Gerenciar perfis',
      'config.situacoes': 'Gerenciar situações',
      'config.unidades': 'Gerenciar unidades',
      'config.meios_entrada': 'Gerenciar meios de entrada',
      'config.meios_saida': 'Gerenciar meios de saída',
      'config.decisoes': 'Gerenciar decisões',
      'config.modulos': 'Gerenciar módulos',
      'config.orgaos': 'Gerenciar órgãos',
      'config.especialistas': 'Gerenciar cadastro de especialistas'
    };

    return prettyNames[permissionName] || permissionName;
  };

  useEffect(() => {
    let abortController = new AbortController();

    const fetchPerfis = async () => {
      setPerfisLoading(true);
      setPerfisError(false);
      
      try {
        const response = await api.get('/perfis', { 
          signal: abortController.signal 
        });
        
        if (response?.data?.data) {
          // Para cada perfil, busca suas permissões específicas
          const perfisWithPermissions = await Promise.all(
            response.data.data.map(async (perfil) => {
              try {
                // Busca as permissões do perfil
                const permResponse = await api.get(`/perfis/${perfil.id}/permissions`, {
                  signal: abortController.signal
                });
                
                // Busca o catálogo completo de permissões com descriptions
                const allPermissionsResponse = await api.get('/permissions', {
                  signal: abortController.signal
                });
                
                let permissions = [];
                
                // O PerfilResource retorna permissions diretamente no objeto, não em data.permissions
                const permissionNames = permResponse?.data?.data?.permissions || [];
                
                if (Array.isArray(permissionNames)) {
                  // Cria um mapa de permissões com suas descriptions
                  const permissionsMap = {};
                  if (allPermissionsResponse?.data?.data) {
                    allPermissionsResponse.data.data.forEach(p => {
                      permissionsMap[p.name] = p.description || p.name;
                    });
                  }

                  // Converte os nomes das permissões para objetos com 'nome' (description)
                  permissions = permissionNames.map(permName => {
                    const description = permissionsMap[permName] || permName;
                    return { nome: description };
                  });
                  
                  // Se não há permissões na API, mostra mensagem apropriada
                  if (permissions.length === 0) {
                    permissions = [{ nome: 'Nenhuma permissão configurada' }];
                  }
                } else {
                  permissions = [{ nome: 'Erro ao carregar permissões' }];
                }
                
                return {
                  ...perfil,
                  permissions: permissions
                };
              } catch (err) {
                // Se não conseguir buscar permissões, retorna o perfil com permissões padrão
                return {
                  ...perfil,
                  permissions: getDefaultPermissions(perfil.nome)
                };
              }
            })
          );

          // Ordena os perfis por ordem de importância/hierarquia
          const sortedPerfis = perfisWithPermissions.sort((a, b) => {
            const hierarchy = { 'Administrador': 0, 'Operador': 1, 'Consulta': 2 };
            return (hierarchy[a.nome] ?? 999) - (hierarchy[b.nome] ?? 999);
          });
          setPerfis(sortedPerfis);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          setPerfisError(true);
          // Fallback: Define perfis estáticos básicos com permissões padrão
          setPerfis([
            { 
              id: 1, 
              nome: 'Administrador', 
              permissions: getDefaultPermissions('Administrador')
            },
            { 
              id: 2, 
              nome: 'Operador', 
              permissions: getDefaultPermissions('Operador')
            },
            { 
              id: 3, 
              nome: 'Consulta', 
              permissions: getDefaultPermissions('Consulta')
            }
          ]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setPerfisLoading(false);
        }
      }
    };

    fetchPerfis();
    
    return () => {
      abortController.abort();
    };
  }, []);

  // Função helper para determinar cor do perfil
  const getPerfilColor = (nome) => {
    const colors = {
      'Administrador': 'danger',
      'Operador': 'success', 
      'Consulta': 'primary',
      'Técnico': 'success',
      'MPDFT': 'primary',
      'DPDF': 'warning',
      'Especialista': 'info'
    };
    return colors[nome] || 'secondary';
  };

  // Função helper para ícone do perfil
  const getPerfilIcon = (nome) => {
    const icons = {
      'Administrador': 'shield-fill-exclamation',
      'Operador': 'person-check',
      'Consulta': 'eye-fill',
      'Técnico': 'person-check',
      'MPDFT': 'file-earmark-text',
      'DPDF': 'scale',
      'Especialista': 'clipboard-pulse'
    };
    return icons[nome] || 'person-badge';
  };

  // Função helper para descrição do perfil
  const getPerfilDescription = (perfil) => {
    const staticDescriptions = {
      'Administrador': 'Acesso total ao sistema, gerenciamento de usuários e configurações.',
      'Operador': 'Operações principais: adolescentes, movimentações e atendimentos.',
      'Consulta': 'Visualização de dados e geração de relatórios básicos.',
      'Técnico': 'Operações técnicas e cadastros operacionais.',
      'MPDFT': 'Consulta e relatórios específicos do Ministério Público.',
      'DPDF': 'Consulta e relatórios específicos da Defensoria Pública.',
      'Especialista': 'Atendimentos técnicos específicos da área.'
    };
    
    const description = staticDescriptions[perfil.nome];
    const permissionsCount = perfil.permissions?.length || 0;
    
    if (description) {
      return `${description} (${permissionsCount} permissões)`;
    }
    
    return `Perfil com ${permissionsCount} permissões específicas.`;
  };

  // Função para renderizar as permissões de forma "pretty"
  const renderPermissions = (permissions, isLoading = false) => {
    if (isLoading) {
      return (
        <div className="text-muted small mt-2">
          <i className="bi bi-clock me-1"></i>
          Carregando permissões...
        </div>
      );
    }
    
    if (!permissions || permissions.length === 0) {
      return (
        <div className="text-muted small mt-2">
          <i className="bi bi-info-circle me-1"></i>
          Nenhuma permissão definida
        </div>
      );
    }

    // Mostra todas as permissões (sem limitação)
    return (
      <div className="mt-2">
        <div className="d-flex flex-wrap gap-1">
          {permissions.map((perm, index) => {
            // Se é um objeto com propriedade 'nome', usa essa propriedade
            // Se é uma string direta (vem da API), converte para pretty
            const permissionText = perm.nome || prettyPermissionName(perm);
            
            return (
              <span 
                key={index} 
                className="badge bg-secondary bg-opacity-25 text-light small"
                style={{ fontSize: '0.7rem' }}
              >
                {permissionText}
              </span>
            );
          })}
        </div>
      </div>
    );
  };
  return (
    <Container fluid className="py-4">
      <ManualBreadcrumb 
        items={[
          { label: 'Manual', path: '/painel/manual' },
          { label: 'Usuários', path: '/painel/manual/usuarios' }
        ]}
      />

      <div className="manual-header">
        <h1 className="manual-title">
          <i className="bi bi-person-badge me-3"></i>
          Gerenciamento de Usuários
        </h1>
        <p className="manual-subtitle">
          Aprenda a cadastrar, editar e gerenciar usuários do sistema
        </p>
      </div>

      <ManualAlert type="info">
        <strong>Quem pode gerenciar usuários?</strong> O acesso depende das permissões
        <code>usuarios.view</code>, <code>usuarios.create</code>, <code>usuarios.edit</code> e
        <code>usuarios.delete</code>. Se você não vê o menu "Usuários", seu perfil não possui a
        permissão necessária.
      </ManualAlert>

      <ManualSection icon="bi-person-plus" title="Cadastrando um Novo Usuário">
        <p className="mb-4">
          O cadastro de usuários permite adicionar novos colaboradores ao sistema e definir 
          suas permissões de acesso.
        </p>

        <ManualStep number={1}>
          <div>
            <strong>Acesse o Menu de Usuários</strong>
            <p className="mb-0">
              No menu lateral, clique em <strong>"Tabelas de Apoio"</strong> e depois em 
              <strong>"Usuários"</strong>. Você verá a lista de todos os usuários cadastrados.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={2}>
          <div>
            <strong>Clique em "Novo Usuário"</strong>
            <p className="mb-0">
              No canto superior direito da tela, clique no botão verde <strong>"+ Novo Usuário"</strong>. 
              O formulário de cadastro será aberto.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={3}>
          <div>
            <strong>Preencha os Dados Pessoais</strong>
            <div className="mt-2 manual-service-card">
              <p className="mb-2"><strong>Informações obrigatórias:</strong></p>
              <ul className="mb-0">
                <li><strong>E-mail:</strong> usado para identificar o usuário e verificar tipo de autenticação</li>
                <li><strong>Nome Completo:</strong> preenchido manualmente ou retornado pela busca LDAP</li>
                <li><strong>Perfil:</strong> define as permissões efetivas do usuário</li>
                <li><strong>Órgão:</strong> vínculo institucional do usuário</li>
              </ul>
            </div>
          </div>
        </ManualStep>

        <ManualStep number={4}>
          <div>
            <strong>Configure o Tipo de Autenticação</strong>
            <div className="mt-2 manual-help-box">
              <p className="mb-2"><strong>Tipos disponíveis:</strong></p>
              <ul className="mb-0">
                <li>
                  <strong>LDAP (Rede Interna):</strong> Para usuários encontrados no diretório da SEJUS-DF.
                  O sistema consulta o e-mail e usa a senha da rede corporativa.
                </li>
                <li>
                  <strong>Externo (E-mail/Senha):</strong> Para usuários externos (MPDFT, DPDF) que 
                  usam e-mail e senha próprios. O sistema enviará um e-mail para definir a senha
                </li>
              </ul>
            </div>
          </div>
        </ManualStep>

        <ManualStep number={5}>
          <div>
            <strong>Revise antes de salvar</strong>
            <p className="mb-0">
              Confirme e-mail, nome, perfil e órgão. O sistema impede e-mails duplicados e valida
              se o perfil e o órgão existem.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={6}>
          <div>
            <strong>Salve o Cadastro</strong>
            <p className="mb-0">
              Clique em <strong>"Salvar"</strong> no final do formulário. O usuário será criado 
              e receberá um e-mail de boas-vindas com instruções de acesso.
            </p>
          </div>
        </ManualStep>
      </ManualSection>

      <ManualSection icon="bi-shield-check" title="Perfis de Usuário">
        <p className="mb-4">
          Cada usuário recebe um perfil que define quais funcionalidades ele pode acessar no sistema:
        </p>

        {perfisLoading && (
          <div className="text-center py-4">
            <LoadingSpinner text="Carregando perfis atualizados..." />
          </div>
        )}

        {!perfisLoading && (
          <>
            <div className="row g-3 mb-4">
              {perfis.map((perfil) => (
                <div key={perfil.id} className="col-md-6">
                  <div className="manual-profile-card">
                    <h6 className={`text-${getPerfilColor(perfil.nome)} mb-2`}>
                      <i className={`bi bi-${getPerfilIcon(perfil.nome)} me-2`}></i>
                      {perfil.nome}
                    </h6>
                    <p className="mb-1 small">
                      <strong>{getPerfilDescription(perfil)}</strong>
                    </p>
                    
                    {/* Renderização das permissões de forma "pretty" */}
                    <div className="mb-2">
                      <small className="text-secondary">Permissões do perfil:</small>
                      {renderPermissions(perfil.permissions)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <ManualAlert type={perfisError ? "warning" : "tip"}>
              {perfisError ? (
                <>
                  <strong>Usando dados padrão:</strong> Não foi possível carregar os perfis mais recentes do servidor. 
                  Os dados exibidos são baseados na configuração padrão do sistema.
                  {canAccess('config', 'perfis') && (
                    <>
                      {' '}Para ver a lista completa e atualizada com todas as permissões, acesse {' '}
                      <Link to="/painel/perfis" className="alert-link">
                        <strong>Gerenciar Perfis</strong>
                      </Link>.
                    </>
                  )}
                </>
              ) : (
                <>
                  <strong>Dados atualizados!</strong> Os perfis exibidos acima estão sincronizados com o sistema.
                  {canAccess('config', 'perfis') && (
                    <>
                      {' '}Para gerenciar perfis e permissões, acesse {' '}
                      <Link to="/painel/perfis" className="alert-link">
                        <strong>Gerenciar Perfis</strong>
                      </Link>.
                    </>
                  )}
                </>
              )}
            </ManualAlert>
          </>
        )}
      </ManualSection>

      <ManualSection icon="bi-pencil" title="Editando Usuários">
        <ManualStep number={1}>
          <div>
            <strong>Localize o Usuário</strong>
            <p className="mb-0">
              Na lista de usuários, use a barra de busca para encontrar o usuário que deseja editar. 
              Você pode buscar por nome, e-mail ou perfil.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={2}>
          <div>
            <strong>Clique em Editar</strong>
            <p className="mb-0">
              Na linha do usuário, clique no ícone de edição (lápis amarelo) na coluna "Ações". 
              O formulário será aberto com os dados atuais. O próprio usuário logado não consegue
              editar a si mesmo pela lista administrativa.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={3}>
          <div>
            <strong>Altere as Informações</strong>
              <p className="mb-0">
                Modifique os campos desejados. Você pode alterar perfil, e-mail, nome e órgão.
                O tipo LDAP/externo é enviado ao servidor conforme a verificação feita pelo e-mail.
              </p>
          </div>
        </ManualStep>

        <ManualStep number={4}>
          <div>
            <strong>Salve as Alterações</strong>
            <p className="mb-0">
              Clique em <strong>"Salvar"</strong>. As mudanças entrarão em vigor imediatamente. 
              Se você alterou o perfil, o usuário terá suas permissões atualizadas a partir da
              próxima sincronização da sessão.
            </p>
          </div>
        </ManualStep>

        <ManualAlert type="warning">
          <strong>Atenção ao alterar perfis:</strong> Quando você muda o perfil de um usuário, 
          suas permissões são modificadas imediatamente. Se você remover permissões que o usuário 
          está usando no momento, ele pode perder acesso a certas telas.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-shield-lock" title="Gerenciando Permissões por Perfil">
        <p className="mb-4">
          As permissões são gerenciadas por perfil, não individualmente por usuário. Ao mudar as
          permissões de um perfil, todos os usuários daquele perfil passam a seguir a nova matriz.
        </p>

        <ManualStep number={1}>
          <div>
            <strong>Acesse Perfis</strong>
            <p className="mb-0">
              No menu <strong>Tabelas de Apoio</strong>, abra <strong>Perfis</strong> e clique no
              ícone de permissões do perfil desejado.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={2}>
          <div>
            <strong>Revise as Permissões do Perfil</strong>
            <p className="mb-0">
              A tela mostrará todas as permissões disponíveis para o perfil. Você verá permissões
              como "Visualizar Adolescentes", "Criar Movimentações", "Excluir Usuários", etc.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={3}>
          <div>
            <strong>Personalize se Necessário</strong>
            <div className="mt-2 manual-help-box">
              <p className="mb-2"><strong>Você pode:</strong></p>
              <ul className="mb-0">
                <li>Adicionar permissões ao perfil</li>
                <li>Remover permissões do perfil, com cuidado</li>
                <li>Criar perfis distintos para grupos de trabalho específicos</li>
              </ul>
            </div>
          </div>
        </ManualStep>

        <ManualAlert type="important">
          <strong>Boas Práticas:</strong> prefira perfis claros e reutilizáveis. Alterar um perfil
          impacta todos os usuários vinculados a ele.
          {canAccess('config', 'perfis') && (
            <>
              {' '}Para entender melhor o sistema de permissões, consulte {' '}
              <Link to="/painel/perfis" className="alert-link">
                <strong>Gerenciar Perfis</strong>
              </Link>.
            </>
          )}
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-x-circle" title="Excluindo Usuários">
        <p className="mb-4">
          Quando um usuário não deve mais acessar o sistema, a lista administrativa permite exclusão
          para quem possui <code>usuarios.delete</code>.
        </p>

        <div className="manual-troubleshoot">
          <h6 className="text-warning mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Antes de excluir
          </h6>
          <ul className="mb-0">
            <li>Confirme que o usuário realmente não deve acessar o sistema</li>
            <li>Revise impactos em auditoria e histórico de ações</li>
            <li>Evite excluir usuários usados como referência operacional sem alinhamento administrativo</li>
          </ul>
        </div>

        <div className="mt-4">
          <strong>Para excluir:</strong>
          <ol className="mt-2">
            <li>Localize o usuário na lista</li>
            <li>Clique no ícone de lixeira</li>
            <li>Confirme a ação solicitada pelo sistema</li>
          </ol>
          <p className="mb-0 text-muted small mt-2">
            Se o botão estiver desabilitado, seu perfil não possui permissão para excluir aquele registro.
          </p>
        </div>
      </ManualSection>

      <ManualSection icon="bi-key" title="Redefinindo Senhas">
        <ManualAlert type="info">
          <strong>Usuários LDAP (rede interna):</strong> A senha é a mesma da rede corporativa. 
          Para redefinir, o usuário deve entrar em contato com o suporte de TI da SEJUS-DF.
        </ManualAlert>

        <ManualAlert type="info">
          <strong>Usuários Externos (e-mail/senha):</strong> Se um usuário externo esquecer a senha, 
          ele pode usar a opção "Esqueci minha senha" na tela de login. Um e-mail será enviado com 
          instruções para criar uma nova senha.
        </ManualAlert>

        <div className="manual-help-box mt-3">
          <h6 className="mb-3">Criação de senha de usuário externo:</h6>
          <p className="mb-2">
            Ao criar um usuário externo, o sistema gera uma senha temporária segura e envia e-mail
            de boas-vindas com instruções de acesso.
          </p>
        </div>
      </ManualSection>

      <ManualAlert type="tip">
        <strong>Dica de Segurança:</strong> Revise periodicamente a lista de usuários e remova
        acessos que não devem mais existir, seguindo o fluxo administrativo da sua unidade.
      </ManualAlert>
    </Container>
  );
}

export default ManualUsuariosPage;
